import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../../../utils/api';
import ActionButton from '../../ActionButton';

const FiltersBar = ({
  filters,
  onChange,
  onApply,
  onHierarchyChange,
  showStation = true,
  showDaypart = true,
  showDates = true,
  showActions = true,
  trailingContent = null,
  ariaLabel = 'Report filters',
  className = '',
  availableStartDate = '',
  availableEndDate = '',
  earliestDate = '',
  latestDate = '',
  defaultRangeLabel = '',
  resetValues = null,
  defaultStartDate = '',
  defaultEndDate = '',
}) => {
  const [hierarchyRows, setHierarchyRows] = useState([]);
  useEffect(() => { onHierarchyChange?.(hierarchyRows); }, [hierarchyRows, onHierarchyChange]);

  const [uiFilters, setUiFilters] = useState({
    site: '',
    location: '',
    sublocation: ''
  });

  const [localFilters, setLocalFilters] = useState(filters);

  // Sync from parent
  useEffect(() => {
    setLocalFilters(filters);
    setUiFilters({
      site: filters?.site_id || '',
      location: filters?.location_id || '',
      sublocation: filters?.sublocation_id || ''
    });
  }, [filters]);

  // Fetch hierarchy rows from the new reports router
  useEffect(() => {
    const fetchHierarchy = async () => {
      try {
        const res = await apiFetch('/updates-reports/hierarchy');
        const data = await res.json();
        setHierarchyRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching reports hierarchy:', err);
        setHierarchyRows([]);
      }
    };
    fetchHierarchy();
  }, []);

  const sites = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.site_id == null) continue;
      if (!map.has(row.site_id)) {
        map.set(row.site_id, row.site_name || `Site ${row.site_id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [hierarchyRows]);

  const locations = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.location_id == null) continue;
      if (uiFilters.site && String(row.site_id) !== String(uiFilters.site)) continue;
      if (!map.has(row.location_id)) {
        map.set(row.location_id, row.location_name || `Location ${row.location_id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [hierarchyRows, uiFilters.site]);

  const sublocations = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.sublocation_id == null) continue;
      if (uiFilters.site && String(row.site_id) !== String(uiFilters.site)) continue;
      if (uiFilters.location && String(row.location_id) !== String(uiFilters.location)) continue;
      if (!map.has(row.sublocation_id)) {
        map.set(row.sublocation_id, row.sublocation_name || `Sublocation ${row.sublocation_id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [hierarchyRows, uiFilters.site, uiFilters.location]);

  const stations = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.station_id == null) continue;
      if (uiFilters.site && String(row.site_id) !== String(uiFilters.site)) continue;
      if (uiFilters.location && String(row.location_id) !== String(uiFilters.location)) continue;
      if (uiFilters.sublocation && String(row.sublocation_id) !== String(uiFilters.sublocation)) continue;
      if (!map.has(row.station_id)) {
        map.set(row.station_id, row.station_name || '');
      }
    }
    return Array.from(map.entries()).map(([station_id, station_name]) => ({
      station_id: String(station_id),
      station_name
    }));
  }, [hierarchyRows, uiFilters.site, uiFilters.location, uiFilters.sublocation]);

  // If selected station is no longer valid after cascade changes, clear it.
  useEffect(() => {
    const selectedStation = String(localFilters.station || '');
    if (!selectedStation) return;
    const exists = stations.some((st) => String(st.station_id) === selectedStation);
    if (!exists) {
      const updated = { ...localFilters, station: '' };
      setLocalFilters(updated);
      onChange(updated);
    }
  }, [stations, localFilters, onChange]);

  // Handle UI filter changes
  const handleUiChange = (e) => {
    const { name, value } = e.target;

    if (name === 'site') {
      const nextUi = { site: value, location: '', sublocation: '' };
      const updated = {
        ...localFilters,
        site_id: value || '',
        location_id: '',
        sublocation_id: '',
        station: ''
      };
      setUiFilters(nextUi);
      setLocalFilters(updated);
      onChange(updated);
    }
    if (name === 'location') {
      const nextUi = { ...uiFilters, location: value, sublocation: '' };
      const updated = {
        ...localFilters,
        site_id: nextUi.site || '',
        location_id: value || '',
        sublocation_id: '',
        station: ''
      };
      setUiFilters(nextUi);
      setLocalFilters(updated);
      onChange(updated);
    }
    if (name === 'sublocation') {
      const nextUi = { ...uiFilters, sublocation: value };
      const updated = {
        ...localFilters,
        site_id: nextUi.site || '',
        location_id: nextUi.location || '',
        sublocation_id: value || '',
        station: ''
      };
      setUiFilters(nextUi);
      setLocalFilters(updated);
      onChange(updated);
    }
  };

  // Handle actual filters (station + dates)
  const handleChange = (e) => {
    const updated = { ...localFilters, [e.target.name]: e.target.value };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleApplyFilters = () => {
    const appliedFilters = {
      station: localFilters.station || '',
      startDate: localFilters.startDate || '',
      endDate: localFilters.endDate || '',
      daypart: localFilters.daypart || '',
      site_id: uiFilters.site || '',
      location_id: uiFilters.location || '',
      sublocation_id: uiFilters.sublocation || ''
    };

    onApply?.(appliedFilters);
  };

  const handleClearFilters = () => {
    const resetFilters = resetValues || {
      station: '',
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      daypart: '',
      site_id: '',
      location_id: '',
      sublocation_id: ''
    };

    setUiFilters({
      site: '',
      location: '',
      sublocation: ''
    });
    setLocalFilters(resetFilters);
    onChange(resetFilters);
    onApply?.(resetFilters);
  };

  return (
    <div className={`reports-filters flex flex-wrap gap-2.5 mt-4 items-center ${className}`} role="group" aria-label={ariaLabel}>
      {/* Site Dropdown */}
      <select
        name="site"
        aria-label="Site"
        value={uiFilters.site}
        onChange={handleUiChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      >
        <option value="">All Sites</option>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>

      {/* Location Dropdown */}
      <select
        name="location"
        aria-label="Location"
        value={uiFilters.location}
        onChange={handleUiChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        disabled={!uiFilters.site}
      >
        <option value="">All Locations</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>

      {/* Sublocation Dropdown */}
      <select
        name="sublocation"
        aria-label="Sublocation"
        value={uiFilters.sublocation}
        onChange={handleUiChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        disabled={!uiFilters.location}
      >
        <option value="">All Sublocations</option>
        {sublocations.map((sl) => (
          <option key={sl.id} value={sl.id}>{sl.name}</option>
        ))}
      </select>

      {/* Station Dropdown */}
      {showStation && <select
        name="station"
        aria-label="Screen"
        value={localFilters.station || ''}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        disabled={!stations.length}
      >
        <option value="">All Stations</option>
        {stations.map((st) => (
          <option key={st.station_id} value={st.station_id}>
            {st.station_name ? `${st.station_name} (${st.station_id})` : st.station_id}
          </option>
        ))}
      </select>}

      {/* Meal Period Dropdown */}
      {showDaypart && <select
        name="daypart"
        aria-label="Meal period"
        value={localFilters.daypart || ''}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      >
        <option value="">All Meal Periods</option>
        <option value="Breakfast">Breakfast</option>
        <option value="Lunch">Lunch</option>
        <option value="Dinner">Dinner</option>
        <option value="Late Night">Late Night</option>
      </select>}

      {/* Date Pickers */}
      {showDates && <input
        type="date"
        name="startDate"
        aria-label="Start date"
        value={localFilters.startDate || ''}
        min={earliestDate || availableStartDate || undefined}
        max={localFilters.endDate || latestDate || availableEndDate || undefined}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      />}
      {showDates && <input
        type="date"
        name="endDate"
        aria-label="End date"
        value={localFilters.endDate || ''}
        min={localFilters.startDate || earliestDate || availableStartDate || undefined}
        max={latestDate || availableEndDate || undefined}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      />}
      {showDates && defaultRangeLabel && <span className="text-xs text-gray-500 dark:text-gray-400">{defaultRangeLabel}{(earliestDate || availableStartDate) ? ` · history from ${earliestDate || availableStartDate}` : ''}</span>}

      {showActions && <ActionButton
        type="button"
        onClick={handleApplyFilters}
      >
        APPLY
      </ActionButton>}

      {showActions && <ActionButton
        type="button"
        variant="outlined"
        onClick={handleClearFilters}
      >
        RESET
      </ActionButton>}

      {trailingContent}
    </div>
  );
};

export default FiltersBar;
